import React, {
  useState,
  useMemo,
  useImperativeHandle,
  ReactNode,
  ReactElement,
} from 'react';

import { Alert } from '@ast/magma/components/alert';
import { DialogProps } from '@ast/magma/components/dialog';

import { PortalDialogForm, PortalDialogFormProps } from '@app/common/components/configurable-wizards/PortalDialogForm';
import { Alert as AlertType } from '@app/common/components/Notifier';
import { OverlaySpinner } from '@app/common/components/overlayspinner';
import { FieldInfoGroupField } from '@app/common/configurable-wizards';
import { PhoneExtensionFieldLocator } from '@app/common/configurable-wizards/fields/Phone';
import { ZipCodeFieldLocator } from '@app/common/configurable-wizards/fields/ZIP/ZipCodeField';
import { Field } from '@app/common/configurable-wizards/types';

import { ConditionalAddressFieldLocator } from '@app/widgets/contacts-and-settings/fields/Address/ConditionalAddressField';
import { ConstrainedAddressFieldLocator } from '@app/widgets/contacts-and-settings/fields/Address/ConstrainedAddressField';
import { CountryFieldLocator } from '@app/widgets/contacts-and-settings/fields/Address/CountryField';
import { DetailEmailFieldLocator } from '@app/widgets/contacts-and-settings/fields/Email/EmailFieldDetails';
import { GenericPhoneFieldLocator } from '@app/widgets/contacts-and-settings/fields/Phone/GenericPhone/GenericPhoneField';
import { OnErrorProps, UseSubmitContact, useSubmitContact } from '@app/widgets/contacts-and-settings/hooks/useSubmitContact';
import { useUserSettingsLocator } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsLocator';
import { AddressFormCache } from '@app/widgets/contacts-and-settings/types';
import { mapErrorsToAlerts, ServerErrors } from '@app/widgets/contacts-and-settings/utils/errorsMapper';
import { transformFields } from '@app/widgets/contacts-and-settings/utils/subFieldsTransformer';
import { MilitaryMailingCitiesLocator, StatesFieldLocator } from '@app/widgets/contacts-and-settings/utils/submitLocators';

import styles from './SubmitModal.pcss';

export type SubmitModalProps = Pick<UseSubmitContact, 'onError'> & {
  readonly field: FieldInfoGroupField;
  readonly formOptions?: PortalDialogFormProps['formOptions'];
  readonly dialogOptions: DialogProps;
  readonly actionButton?: ReactElement;
  readonly successMessage?: string;
  readonly markAsPrimary?: boolean;
  readonly children?: ReactNode;
  readonly open?: boolean;
  readonly onClose?: () => void;
};

export type RefHandlers = {
  onClick: () => void
};

export const SubmitModal = React.forwardRef<RefHandlers, SubmitModalProps>((
  props,
  ref,
) => {
  const {
    field,
    actionButton,
    formOptions,
    dialogOptions,
    successMessage,
    markAsPrimary,
    children,
    open = false,
    onClose,
    onError,
  } = props;

  const [isOpen, setIsOpen] = useState(open);
  const [errorAlerts, setErrorAlerts] = useState<AlertType[]>([]);
  const subFields = useMemo(() => transformFields(field, markAsPrimary), [field]);

  const locator = useUserSettingsLocator([
    ConstrainedAddressFieldLocator,
    ConditionalAddressFieldLocator,
    GenericPhoneFieldLocator,
    CountryFieldLocator,
    StatesFieldLocator,
    MilitaryMailingCitiesLocator,
    PhoneExtensionFieldLocator,
    DetailEmailFieldLocator,
    ZipCodeFieldLocator,
  ]);

  const openDialog = () => {
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setErrorAlerts([]);

    if (onClose) {
      onClose();
    }
  };

  const handleError = (errorProps: OnErrorProps) => {
    setErrorAlerts(mapErrorsToAlerts(errorProps.errors as ServerErrors));

    if (onError) {
      onError(errorProps);
    }
  };

  const { isLoading, submitContact } = useSubmitContact({
    successMessage,
    onSuccess: closeDialog,
    onError: handleError,
  });

  useImperativeHandle(ref, () => ({
    onClick: openDialog,
  }));

  const handleSubmit = (values: AddressFormCache) => {
    setErrorAlerts([]);
    submitContact(values);
  };

  return (
    <div data-stable-name="SubmitPhoneFieldDialogWrapper">
      {actionButton && React.cloneElement(actionButton, {
        onClick: openDialog,
        onSelect: openDialog,
      })}
      {isOpen && (
        <PortalDialogForm
          key={field.id} // reset form data when field is changed
          formOptions={formOptions || {}}
          dialogOptions={{
            ...dialogOptions,
            className: styles.dialogWrapper,
            onClose: closeDialog,
          }}
          onSubmit={handleSubmit}
        >
          {errorAlerts.length > 0 && (
            <div className={styles.alertsWrapper}>
              {errorAlerts.map((alert, index) => (
                <Alert
                  key={alert.title || index}
                  {...alert}
                />
              ))}
            </div>
          )}
          {children}
          <div className={styles.fieldsWrapper}>
            {subFields.map((item, index) => {
              const subField = item as Field;
              const Component = locator(subField);

              return (
                <Component
                  key={subField.id || index}
                  parent={field.id}
                  field={subField}
                  locator={locator}
                  fieldProps={{
                    containerClassName: styles.fieldContainer,
                    fieldClassName: styles.field,
                  }}
                />
              );
            })}
          </div>
          <OverlaySpinner
            visible={isLoading}
            containerClassName={styles.overlayContainer}
          />
        </PortalDialogForm>
      )}
    </div>
  );
});
